import { SessionChannel } from './SessionChannel';
import type {
  NextGameCreatedMessage,
  ChatMessageEvent,
  ChatHistoryEvent,
  SessionAbandonedMessage,
  ErrorMessage,
} from '../types/game';

/**
 * Single owner of the per-session ActionCable subscription (F7).
 *
 * Previously TWO SessionChannel subscriptions existed for the same session —
 * one opened by React chat (useChatState) and a second opened by Phaser at
 * round-end (GameScene) — with identical identifiers on the same consumer.
 * That duplication risked one unsubscribe disturbing the other's server stream.
 *
 * This hub owns exactly ONE SessionChannel per sessionId, ref-counted. Both the
 * chat consumer and the round-end consumer `acquire()` a handle; the hub
 * multiplexes the underlying channel's single callback slots out to every
 * registered listener. When the last handle is released the channel is
 * unsubscribed. It is the smallest change that gives one coherent session-level
 * owner while leaving each consumer's own logic intact.
 */

type Listener<T> = (data: T) => void;
type VoidListener = () => void;

interface Entry {
  channel: SessionChannel;
  refCount: number;
  // Cleared when the entry is torn down — either by the last release() or by
  // resetSessionChannelHub() (identity reset). A stale handle checks this so it
  // can never double-unsubscribe or mutate a fresh replacement entry (N1).
  alive: boolean;
  nextGameCreated:  Set<Listener<NextGameCreatedMessage>>;
  chatMessage:      Set<Listener<ChatMessageEvent>>;
  chatHistory:      Set<Listener<ChatHistoryEvent>>;
  sessionAbandoned: Set<Listener<SessionAbandonedMessage>>;
  error:            Set<Listener<ErrorMessage>>;
  reconnect:        Set<VoidListener>;
  rejected:         Set<VoidListener>;
}

const entries = new Map<number, Entry>();

/**
 * A per-consumer handle over the shared session channel. Register the events you
 * care about; call release() exactly once when done (unmount / scene teardown).
 * Releasing removes only THIS handle's listeners and drops the shared channel
 * when the last handle releases.
 */
export interface SessionChannelHandle {
  onNextGameCreated(cb: Listener<NextGameCreatedMessage>): this;
  onChatMessage(cb: Listener<ChatMessageEvent>): this;
  onChatHistory(cb: Listener<ChatHistoryEvent>): this;
  onSessionAbandoned(cb: Listener<SessionAbandonedMessage>): this;
  onError(cb: Listener<ErrorMessage>): this;
  onReconnect(cb: VoidListener): this;
  onRejected(cb: VoidListener): this;
  sendMessage(body: string): void;
  fetchHistory(): void;
  requestNextGame(): void;
  release(): void;
}

function createEntry(sessionId: number): Entry {
  const entry: Entry = {
    channel: new SessionChannel(sessionId),
    refCount: 0,
    alive: true,
    nextGameCreated:  new Set(),
    chatMessage:      new Set(),
    chatHistory:      new Set(),
    sessionAbandoned: new Set(),
    error:            new Set(),
    reconnect:        new Set(),
    rejected:         new Set(),
  };

  // Wire the underlying channel's single callback slots to fan out to the sets.
  entry.channel
    .onNextGameCreated((m) => entry.nextGameCreated.forEach((l) => l(m)))
    .onChatMessage((m) => entry.chatMessage.forEach((l) => l(m)))
    .onChatHistory((m) => entry.chatHistory.forEach((l) => l(m)))
    .onSessionAbandoned((m) => entry.sessionAbandoned.forEach((l) => l(m)))
    .onError((m) => entry.error.forEach((l) => l(m)))
    .onReconnect(() => entry.reconnect.forEach((l) => l()))
    .onRejected(() => entry.rejected.forEach((l) => l()));

  entry.channel
    .subscribe()
    .then(() => {
      // If the entry was purged (identity reset) while subscribe() was awaiting
      // the consumer, the purge's unsubscribe() was a no-op (subscription didn't
      // exist yet). Tear the just-created subscription down now so no orphaned
      // SessionChannel survives the purge — keeps "unsubscribe exactly once" (N1).
      if (!entry.alive) entry.channel.unsubscribe();
    })
    .catch((err) =>
      console.error(`[SessionChannelHub] subscribe failed session=${sessionId}:`, err),
    );

  return entry;
}

/**
 * Acquire a handle to the shared session channel for `sessionId`. Creates and
 * subscribes the channel on the first acquire; reuses it for every subsequent
 * acquire of the same id (no duplicate ActionCable subscription).
 */
export function acquireSessionChannel(sessionId: number): SessionChannelHandle {
  let entry = entries.get(sessionId);
  if (!entry) {
    entry = createEntry(sessionId);
    entries.set(sessionId, entry);
  }
  entry.refCount++;
  const owned = entry;

  // Track only THIS handle's listeners so release() removes exactly them.
  const mine = {
    nextGameCreated:  new Set<Listener<NextGameCreatedMessage>>(),
    chatMessage:      new Set<Listener<ChatMessageEvent>>(),
    chatHistory:      new Set<Listener<ChatHistoryEvent>>(),
    sessionAbandoned: new Set<Listener<SessionAbandonedMessage>>(),
    error:            new Set<Listener<ErrorMessage>>(),
    reconnect:        new Set<VoidListener>(),
    rejected:         new Set<VoidListener>(),
  };
  let released = false;

  const handle: SessionChannelHandle = {
    onNextGameCreated(cb) { owned.nextGameCreated.add(cb); mine.nextGameCreated.add(cb); return handle; },
    onChatMessage(cb) { owned.chatMessage.add(cb); mine.chatMessage.add(cb); return handle; },
    onChatHistory(cb) { owned.chatHistory.add(cb); mine.chatHistory.add(cb); return handle; },
    onSessionAbandoned(cb) { owned.sessionAbandoned.add(cb); mine.sessionAbandoned.add(cb); return handle; },
    onError(cb) { owned.error.add(cb); mine.error.add(cb); return handle; },
    onReconnect(cb) { owned.reconnect.add(cb); mine.reconnect.add(cb); return handle; },
    onRejected(cb) { owned.rejected.add(cb); mine.rejected.add(cb); return handle; },
    sendMessage(body) { owned.channel.sendMessage(body); },
    fetchHistory() { owned.channel.fetchHistory(); },
    requestNextGame() { owned.channel.nextGame(); },
    release() {
      if (released) return;
      released = true;
      // Remove only this handle's listeners (safe whether or not the entry is alive).
      mine.nextGameCreated.forEach((l) => owned.nextGameCreated.delete(l));
      mine.chatMessage.forEach((l) => owned.chatMessage.delete(l));
      mine.chatHistory.forEach((l) => owned.chatHistory.delete(l));
      mine.sessionAbandoned.forEach((l) => owned.sessionAbandoned.delete(l));
      mine.error.forEach((l) => owned.error.delete(l));
      mine.reconnect.forEach((l) => owned.reconnect.delete(l));
      mine.rejected.forEach((l) => owned.rejected.delete(l));

      // Entry already torn down — by resetSessionChannelHub() (identity reset) or
      // a prior last-release. A stale release must NOT double-unsubscribe, touch
      // the refcount, or delete a fresh replacement entry for the same id (N1).
      if (!owned.alive) return;

      owned.refCount--;
      if (owned.refCount <= 0) {
        owned.alive = false;
        owned.channel.unsubscribe();
        // Only delete if the map still points at THIS entry (never a replacement).
        if (entries.get(sessionId) === owned) entries.delete(sessionId);
      }
    },
  };
  return handle;
}

/**
 * Purge ALL session-channel entries. Called by the central realtime teardown
 * (AuthContext) on logout / account-identity change so no identity-bound session
 * subscription survives (N1). Unsubscribes each live underlying SessionChannel
 * exactly once, marks entries dead so any handle acquired before the purge
 * becomes inert (its release() is a no-op), and clears the map. A subsequent
 * acquireSessionChannel(sameId) then builds a fresh SessionChannel on the fresh
 * authenticated consumer.
 */
export function resetSessionChannelHub(): void {
  for (const entry of entries.values()) {
    if (!entry.alive) continue;
    entry.alive = false;
    entry.channel.unsubscribe();
  }
  entries.clear();
}

/** Test/diagnostics helper: number of live session channels currently owned. */
export function _activeSessionChannelCount(): number {
  return entries.size;
}
